import { Button, FormFeedback, Input, Table } from "reactstrap";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getExpenseCategoryOptions } from "features/WorkOrders/services/ExpenseCategoryService";

interface Props {
  validation: any;
  readOnly?: boolean;
}

const emptyExpenseRow = () => ({
  expenseCategoryId: "",
  amount: "",
  description: "",
  _categoryLabel: "",
});

export default function WorkOrderExpensesFieldArray({
  validation,
  readOnly = false,
}: Props) {
  const expenses: any[] = validation.values.expenses ?? [];

  const addRow = () => {
    validation.setFieldValue("expenses", [...expenses, emptyExpenseRow()]);
  };

  const removeRow = (idx: number) => {
    validation.setFieldValue(
      "expenses",
      expenses.filter((_: any, i: number) => i !== idx),
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div className="text-uppercase text-muted fw-semibold fs-12">
            Expenses
          </div>
          <p className="text-muted small mb-0 mt-1">
            Actual costs incurred during this work order
          </p>
        </div>
        {!readOnly && (
          <Button
            type="button"
            color="primary"
            size="sm"
            outline
            onClick={addRow}
          >
            <i className="ri-add-line me-1" />
            Add expense
          </Button>
        )}
      </div>
      <div className="border rounded p-3 bg-light bg-opacity-25">
        {expenses.length === 0 ? (
          <p className="text-muted text-center mb-0 py-2">
            No expenses recorded.
            {!readOnly && ' Click "Add expense" to add one.'}
          </p>
        ) : (
          <div className="table-responsive">
            <Table className="table-borderless align-center mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 180 }}>Category</th>
                  <th style={{ minWidth: 120 }}>Amount</th>
                  <th>Description</th>
                  {!readOnly && <th style={{ width: 48 }} />}
                </tr>
              </thead>
              <tbody>
                {expenses.map((exp, idx) => {
                  const prefix = `expenses[${idx}]`;
                  const expErrors: any =
                    (validation.errors.expenses ?? [])[idx] ?? {};
                  const expTouched: any =
                    (validation.touched.expenses ?? [])[idx] ?? {};
                  return (
                    <tr key={idx}>
                      <td>
                        <AsyncPaginatedSelect
                          key={`exp-cat-${idx}`}
                          fetchOptions={getExpenseCategoryOptions}
                          placeholder="Category..."
                          defaultOption={
                            exp.expenseCategoryId && exp._categoryLabel
                              ? {
                                  value: exp.expenseCategoryId,
                                  label: exp._categoryLabel,
                                }
                              : undefined
                          }
                          value={exp.expenseCategoryId || ""}
                          onChange={(val: any) => {
                            validation.setFieldValue(
                              `${prefix}.expenseCategoryId`,
                              val?.value ?? "",
                            );
                            validation.setFieldValue(
                              `${prefix}._categoryLabel`,
                              val?.label ?? "",
                            );
                          }}
                          onBlur={() =>
                            validation.setFieldTouched(
                              `${prefix}.expenseCategoryId`,
                              true,
                            )
                          }
                          isInvalid={
                            !!(
                              expTouched.expenseCategoryId &&
                              expErrors.expenseCategoryId
                            )
                          }
                          isDisabled={readOnly}
                          menuPosition="absolute"
                          menuPlacement="top"
                        />
                        {expTouched.expenseCategoryId &&
                        expErrors.expenseCategoryId ? (
                          <div className="text-danger small mt-1">
                            {expErrors.expenseCategoryId}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          name={`${prefix}.amount`}
                          value={exp.amount ?? ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={!!(expTouched.amount && expErrors.amount)}
                          disabled={readOnly}
                        />
                        {expTouched.amount && expErrors.amount ? (
                          <FormFeedback>{expErrors.amount}</FormFeedback>
                        ) : null}
                      </td>
                      <td>
                        <Input
                          type="text"
                          name={`${prefix}.description`}
                          value={exp.description ?? ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          placeholder="Optional note"
                          disabled={readOnly}
                        />
                      </td>
                      {!readOnly && (
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0"
                            onClick={() => removeRow(idx)}
                          >
                            <i className="ri-delete-bin-line fs-16" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
